/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { logActivity } from './useActivityLogs'
import { listClipCommits } from '@/lib/api/clips'
import type { Clip, CreateClipInput, UpdateClipInput } from '@/types/database'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get auth token from Supabase
 */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  return session.access_token
}

// Fetch all clips for a project
export function useClips(projectId: string | undefined) {
  return useQuery({
    queryKey: ['clips', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      // Supabase enforces a max limit of 1000 rows per request
      // We need to paginate to get all clips
      const allClips: Clip[] = []
      const seenIds = new Set<string>()
      let page = 0
      const pageSize = 1000
      let hasMore = true
      const maxPages = 20 // Safety limit: max 20,000 clips

      while (hasMore && page < maxPages) {
        const from = page * pageSize
        const to = from + pageSize - 1

        const { data, error } = await supabase
          .from('clips')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false })
          .range(from, to)

        if (error) {
          throw error
        }

        if (data && data.length > 0) {
          // Deduplicate clips by ID
          for (const clip of data) {
            if (!seenIds.has(clip.id)) {
              seenIds.add(clip.id)
              allClips.push(clip)
            }
          }

          // If we got less than pageSize, we've reached the end
          if (data.length < pageSize) {
            hasMore = false
          } else {
            page++
          }
        } else {
          hasMore = false
        }
      }

      return allClips as Clip[]
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  })
}

// Fetch single clip
export function useClip(clipId: string | undefined) {
  return useQuery({
    queryKey: ['clips', 'detail', clipId],
    queryFn: async () => {
      if (!clipId) throw new Error('Clip ID is required')

      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('id', clipId)
        .maybeSingle()

      if (error) {
        throw error
      }
      if (!data) {
        throw new Error('Clip not found')
      }
      return data as Clip
    },
    enabled: !!clipId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}

// Create single clip mutation
export function useCreateClip() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clip: CreateClipInput) => {
      const { data, error } = await supabase
        .from('clips')
        .insert([clip])
        .select()
        .maybeSingle()

      if (error) {
        throw error
      }
      if (!data) {
        throw new Error('Failed to create clip')
      }
      return data as Clip
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.project_id] })
    },
  })
}

// Bulk create clips mutation (for import)
export function useCreateClips() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({
      clips,
      updateExisting = false,
    }: {
      clips: CreateClipInput[]
      updateExisting?: boolean
    }) => {
      if (clips.length === 0) {
        throw new Error('No clips to import')
      }

      const projectId = clips[0].project_id

      // OPTIMIZED: Fetch ALL existing clips for the project in ONE query
      // instead of making separate queries for each clip (N+1 problem fix)
      const { data: existingClips, error: fetchError } = await supabase
        .from('clips')
        .select('id, name, tape_name, source_timecode, custom_metadata')
        .eq('project_id', projectId)

      if (fetchError) throw fetchError

      // Create multiple lookup maps for different matching strategies
      // Strategy 1: name|tape_name|source_timecode (for reimporting same ALE)
      const fullKeyMap = new Map(
        (existingClips || []).map(clip => {
          const tapeName = (clip.tape_name?.trim() || 'NULL').toLowerCase()
          const sourceTimecode = (clip.source_timecode?.trim() || 'NULL').toLowerCase()
          const name = clip.name.trim().toLowerCase()
          const key = `${name}|${tapeName}|${sourceTimecode}`
          return [key, clip]
        })
      )

      // Strategy 2: Match by clip name (for when ALE tape matches DB name)
      // This handles clips imported from OTIO where name=filename
      const nameEntries: Array<[string, any]> = []
      for (const clip of existingClips || []) {
        const name = clip.name.trim().toLowerCase()
        const nameWithoutExt = name.replace(/\.(mov|mp4|mxf|avi|r3d|dpx)$/i, '')
        nameEntries.push([name, clip])
        nameEntries.push([nameWithoutExt, clip])
      }
      const nameOnlyMap = new Map(nameEntries)

      // Multi-strategy matching
      const duplicateChecks = clips.map((clip) => {
        const tapeName = (clip.tape_name?.trim() || 'NULL').toLowerCase()
        const sourceTimecode = (clip.source_timecode?.trim() || 'NULL').toLowerCase()
        const name = clip.name.trim().toLowerCase()

        let existingClip = null

        // Strategy 1: Full composite key (name|tape|timecode)
        const fullKey = `${name}|${tapeName}|${sourceTimecode}`
        existingClip = fullKeyMap.get(fullKey)

        // Strategy 2: ALE tape name matches existing clip name
        // Common when ALE is exported after OTIO import where clip.name = filename
        if (!existingClip && tapeName !== 'NULL') {
          existingClip = nameOnlyMap.get(tapeName)
        }

        // Strategy 3: Exact name match
        if (!existingClip) {
          existingClip = nameOnlyMap.get(name)
        }

        return {
          clip,
          isDuplicate: !!existingClip,
          existingClip,
        }
      })

      // Separate new clips from duplicates
      const newClips = duplicateChecks
        .filter(check => !check.isDuplicate)
        .map(check => check.clip)

      const duplicates = duplicateChecks.filter(check => check.isDuplicate)

      let createdClips: Clip[] = []
      let updatedClips: Clip[] = []

      // Insert new clips via backend API (enforces tier limits server-side)
      if (newClips.length > 0) {
        const token = await getAuthToken()
        const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/clips`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clips: newClips.map(clip => ({
              name: clip.name,
              id: clip.nle_unique_id || null,
              type: 0,
              tape_name: clip.tape_name,
              source_timecode: clip.source_timecode,
              duration_frames: clip.duration_frames,
              fps: clip.fps,
              source_file_path: clip.source_file_path,
              tags: clip.tags,
              custom_metadata: clip.custom_metadata,
            })),
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Failed to import clips')
        }

        const result = await response.json()
        createdClips = result.clips as Clip[]
      }

      // Track changes for updated clips (for activity logging and email)
      const updatedClipsWithChanges: Array<{clip: Clip; changes: string[]}> = []

      // Update existing clips if requested
      if (updateExisting && duplicates.length > 0) {
        const updatePromises = duplicates.map(async ({ clip, existingClip }) => {
          if (!existingClip) return null

          // Track which fields are changing
          const changes: string[] = []

          // Merge custom_metadata: existing + new (new values override)
          const existingMetadata = ((existingClip as any).custom_metadata as Record<string, any>) || {}
          const newMetadata = (clip.custom_metadata as Record<string, any>) || {}
          const mergedMetadata = {
            ...existingMetadata,
            ...newMetadata,
          }

          // Check if custom metadata changed
          const existingMetadataKeys = Object.keys(existingMetadata)
          const newMetadataKeys = Object.keys(newMetadata)
          const metadataChanged = newMetadataKeys.some(key =>
            existingMetadata[key] !== newMetadata[key]
          )
          if (metadataChanged || newMetadataKeys.length > existingMetadataKeys.length) {
            changes.push('metadata')
          }

          // Only update fields that are actually provided in the incoming clip
          const updates: any = {
            custom_metadata: mergedMetadata,
          }

          const existingClipAny = existingClip as any
          if (clip.tape_name !== undefined && clip.tape_name !== existingClipAny.tape_name) {
            updates.tape_name = clip.tape_name
            changes.push('tape_name')
          }
          if (clip.source_timecode !== undefined && clip.source_timecode !== existingClipAny.source_timecode) {
            updates.source_timecode = clip.source_timecode
            changes.push('timecode')
          }
          if (clip.duration_frames !== undefined && clip.duration_frames !== existingClipAny.duration_frames) {
            updates.duration_frames = clip.duration_frames
            changes.push('duration')
          }
          if (clip.fps !== undefined && clip.fps !== existingClipAny.fps) {
            updates.fps = clip.fps
            changes.push('fps')
          }
          if (clip.source_file_path !== undefined && clip.source_file_path !== existingClipAny.source_file_path) {
            updates.source_file_path = clip.source_file_path
            changes.push('file_path')
          }
          if (clip.tags !== undefined && JSON.stringify(clip.tags) !== JSON.stringify(existingClipAny.tags)) {
            updates.tags = clip.tags
            changes.push('tags')
          }

          // Update the clip with new metadata and other fields
          const { data, error } = await supabase
            .from('clips')
            .update(updates)
            .eq('id', (existingClip as any).id)
            .select()
            .single()

          if (error) throw error

          const updatedClip = data as Clip
          updatedClipsWithChanges.push({ clip: updatedClip, changes })

          return updatedClip
        })

        const updateResults = await Promise.all(updatePromises)
        updatedClips = updateResults.filter(Boolean) as Clip[]
      }

      return {
        created: createdClips,
        updated: updatedClips,
        updatedWithChanges: updatedClipsWithChanges,
        skipped: updateExisting ? 0 : duplicates.length,
        duplicateClips: duplicates.map(d => d.clip.name),
      }
    },
    onSuccess: async (data) => {
      // Invalidate queries if any clips were created or updated
      if (data.created.length > 0 || data.updated.length > 0) {
        const projectId = data.created.length > 0
          ? data.created[0].project_id
          : data.updated[0].project_id
        queryClient.invalidateQueries({ queryKey: ['clips', projectId] })

        // Prepare detailed clip info for activity and email
        const updatedClipsDetails = data.updatedWithChanges.map(({ clip, changes }) => ({
          name: clip.name,
          clip_id: clip.id,
          tape_name: clip.tape_name || undefined,
          source_timecode: clip.source_timecode || undefined,
          changes,
        }))

        // Log activity with detailed changes
        if (user) {
          await logActivity({
            project_id: projectId,
            action_type: 'clip_import',
            entity_type: 'clip',
            action_metadata: {
              created: data.created.length,
              updated: data.updated.length,
              skipped: data.skipped,
              clip_names: [...data.created, ...data.updated].map(c => c.name),
              created_clips: data.created.map(c => ({
                name: c.name,
                clip_id: c.id,
                tape_name: c.tape_name || undefined,
                source_timecode: c.source_timecode || undefined,
              })),
              updated_clips: updatedClipsDetails,
            },
          })

          // Refetch activity logs AFTER logging completes to force immediate update
          await queryClient.refetchQueries({ queryKey: ['activityLogs', projectId] })

          // Send email notification (don't fail if email fails)
          try {
            const token = await getAuthToken()

            const response = await fetch(`${BACKEND_URL}/api/notifications/ale-import`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                project_id: projectId,
                created: data.created.length,
                updated: data.updated.length,
                skipped: data.skipped,
                // Send ALL clips for CSV attachment (email template will limit display to first 15)
                created_clips: data.created.map(c => ({
                  name: c.name,
                  tape_name: c.tape_name || undefined,
                  source_timecode: c.source_timecode || undefined,
                  duration_frames: c.duration_frames || undefined,
                  fps: c.fps || undefined,
                  source_file_path: c.source_file_path || undefined,
                  tags: c.tags || undefined,
                  custom_metadata: c.custom_metadata || undefined,
                })),
                updated_clips: updatedClipsDetails.map(detail => ({
                  ...detail,
                  // Find the full clip data to include additional fields
                  duration_frames: data.updated.find(u => u.id === detail.clip_id)?.duration_frames || undefined,
                  fps: data.updated.find(u => u.id === detail.clip_id)?.fps || undefined,
                  source_file_path: data.updated.find(u => u.id === detail.clip_id)?.source_file_path || undefined,
                  tags: data.updated.find(u => u.id === detail.clip_id)?.tags || undefined,
                  custom_metadata: data.updated.find(u => u.id === detail.clip_id)?.custom_metadata || undefined,
                })),
              }),
            })

            if (!response.ok) {
              console.warn('Failed to send ALE import email notification:', await response.text())
            }

          } catch (error) {
            console.warn('Failed to send ALE import email notification:', error)
          }
        }
      }
    },
  })
}

// Update clip mutation
export function useUpdateClip() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateClipInput & { id: string }) => {
      // First, get the current clip data to track changes
      const { data: oldClip, error: fetchError } = await supabase
        .from('clips')
        .select()
        .eq('id', id)
        .maybeSingle()

      if (fetchError) {
        throw fetchError
      }

      // Update the clip
      const { data, error } = await supabase
        .from('clips')
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle()

      if (error) {
        throw error
      }
      if (!data) {
        throw new Error('Clip not found or update not allowed')
      }
      return { data, oldClip, updates }
    },
    onSuccess: ({ data, oldClip, updates }) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.project_id] })
      queryClient.invalidateQueries({ queryKey: ['clips', 'detail', data.id] })

      // Log activity (don't await - do it in background)
      if (user && oldClip) {
        // Determine what changed
        const changes: string[] = []
        const oldValues: Record<string, any> = {}
        const newValues: Record<string, any> = {}

        Object.keys(updates).forEach((key) => {
          const oldClipAny = oldClip as any
          const updatesAny = updates as any
          if (oldClipAny[key] !== updatesAny[key]) {
            changes.push(key)
            oldValues[key] = oldClipAny[key]
            newValues[key] = updatesAny[key]
          }
        })

        if (changes.length > 0) {
          // Don't block on activity logging
          logActivity({
            project_id: data.project_id,
            action_type: 'clip_update',
            entity_type: 'clip',
            entity_id: data.id,
            action_metadata: {
              clip_name: data.name,
              name: data.name,
              changes,
              old_values: oldValues,
              new_values: newValues,
            },
          }).then(() => {
            // Invalidate activity logs after logging completes
            queryClient.invalidateQueries({ queryKey: ['activityLogs'] })
          })
        }
      }
    },
  })
}

// Delete clip mutation
export function useDeleteClip() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ clipId, projectId, clipName }: { clipId: string; projectId: string; clipName?: string }) => {
      const { error } = await supabase
        .from('clips')
        .delete()
        .eq('id', clipId)

      if (error) throw error
      return { clipId, projectId, clipName }
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.projectId] })

      // Log activity
      if (user) {
        await logActivity({
          project_id: data.projectId,
          action_type: 'clip_delete',
          entity_type: 'clip',
          entity_id: data.clipId,
          action_metadata: {
            clip_name: data.clipName,
          },
        })
      }
    },
  })
}

// Delete multiple clips mutation
export function useDeleteClips() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clipIds, projectId }: { clipIds: string[]; projectId: string }) => {
      const { error } = await supabase
        .from('clips')
        .delete()
        .in('id', clipIds)

      if (error) throw error
      return { clipIds, projectId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.projectId] })
    },
  })
}

// Bulk update clip tags (add/remove tags for multiple clips)
export function useBulkUpdateClipTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      clipIds,
      addTags,
      removeTags
    }: {
      projectId: string
      clipIds: string[]
      addTags?: string[]
      removeTags?: string[]
    }) => {
      // Fetch current clips to compute new tags and get clip details
      const { data: clips, error: fetchError } = await supabase
        .from('clips')
        .select('id, name, tape_name, tags')
        .in('id', clipIds)

      if (fetchError) throw fetchError
      if (!clips) throw new Error('Failed to fetch clips')

      // Update each clip's tags
      const updates = clips.map(async (clip) => {
        let newTags = clip.tags || []

        // Remove tags first
        if (removeTags && removeTags.length > 0) {
          newTags = newTags.filter((t: string) => !removeTags.includes(t))
        }

        // Add tags
        if (addTags && addTags.length > 0) {
          // Add only tags that aren't already present
          const tagsToAdd = addTags.filter(t => !newTags.includes(t))
          newTags = [...newTags, ...tagsToAdd]
        }

        const { error } = await supabase
          .from('clips')
          .update({ tags: newTags, updated_at: new Date().toISOString() })
          .eq('id', clip.id)

        if (error) throw error
        return clip.id
      })

      await Promise.all(updates)

      // Log activity with clip details
      await logActivity({
        project_id: projectId,
        action_type: 'bulk_tag_update',
        entity_type: 'clip',
        entity_id: clipIds[0],
        action_metadata: {
          clips_count: clipIds.length,
          added_tags: addTags || [],
          removed_tags: removeTags || [],
          clips: clips.map(clip => ({
            clip_id: clip.id,
            name: clip.name,
            tape_name: clip.tape_name
          }))
        }
      })

      return { projectId, clipIds }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.projectId] })
      // Invalidate individual clip detail queries for all updated clips
      data.clipIds.forEach(clipId => {
        queryClient.invalidateQueries({ queryKey: ['clips', 'detail', clipId] })
      })
    }
  })
}

// Bulk update clip status
export function useBulkUpdateClipStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      clipIds,
      status,
      statusType
    }: {
      projectId: string
      clipIds: string[]
      status: string
      statusType: 'vfx_comp' | 'stock_footage'
    }) => {
      // Fetch clip details before updating
      const { data: clips, error: fetchError } = await supabase
        .from('clips')
        .select('id, name, tape_name')
        .in('id', clipIds)

      if (fetchError) throw fetchError
      if (!clips) throw new Error('Failed to fetch clips')

      const { error } = await supabase
        .from('clips')
        .update({
          status,
          status_type: statusType,
          updated_at: new Date().toISOString()
        })
        .in('id', clipIds)

      if (error) throw error

      // Log activity with clip details
      await logActivity({
        project_id: projectId,
        action_type: 'bulk_status_update',
        entity_type: 'clip',
        entity_id: clipIds[0],
        action_metadata: {
          clips_count: clipIds.length,
          status,
          status_type: statusType,
          clips: clips.map(clip => ({
            clip_id: clip.id,
            name: clip.name,
            tape_name: clip.tape_name
          }))
        }
      })

      return { projectId, clipIds }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clips', data.projectId] })
      // Invalidate individual clip detail queries for all updated clips
      data.clipIds.forEach(clipId => {
        queryClient.invalidateQueries({ queryKey: ['clips', 'detail', clipId] })
      })
    }
  })
}

// Fetch commits that reference a clip
export function useClipCommits(clipId: string | undefined) {
  return useQuery({
    queryKey: ['clips', 'commits', clipId],
    queryFn: async () => {
      if (!clipId) throw new Error('Clip ID is required')
      try {
        return await listClipCommits(clipId)
      } catch (error: any) {
        // If clip not found (404), return empty result instead of throwing
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          console.warn(`[useClipCommits] Clip ${clipId} not found, returning empty commits`)
          return {
            clip_id: clipId,
            clip_name: 'Unknown',
            associations: [],
            total: 0
          }
        }
        throw error
      }
    },
    enabled: !!clipId,
    retry: false, // Don't retry 404 errors
  })
}

// Assign clips to a team member
export function useAssignClips() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({
      projectId,
      clipIds,
      assignedUserId,
    }: {
      projectId: string
      clipIds: string[]
      assignedUserId: string | null
    }) => {
      const { assignClips } = await import('@/lib/api/clips')
      return assignClips(projectId, {
        clip_ids: clipIds,
        assigned_user_id: assignedUserId,
      })
    },
    onSuccess: async (_data, variables) => {
      // Invalidate clips queries and await refetch
      await queryClient.invalidateQueries({ queryKey: ['clips', variables.projectId] })

      // Invalidate individual clip queries
      await Promise.all(
        variables.clipIds.map(clipId =>
          queryClient.invalidateQueries({ queryKey: ['clips', 'detail', clipId] })
        )
      )

      // Invalidate dashboard queries (so AssignedToMe section updates)
      // Use prefix matching to invalidate all user-specific assignment queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'dashboard' &&
            (queryKey[1] === 'myAssignedClips' ||
             queryKey[1] === 'myAssignedVfxShots' ||
             (queryKey[1] === 'projectAssignedClips' && queryKey[2] === variables.projectId) ||
             (queryKey[1] === 'projectAssignedVfxShots' && queryKey[2] === variables.projectId))
          )
        },
      })

      // Log activity
      if (user) {
        await logActivity({
          project_id: variables.projectId,
          action_type: 'clip_update',
          entity_type: 'clip',
          action_metadata: {
            action: 'assignment',
            clip_ids: variables.clipIds,
            assigned_user_id: variables.assignedUserId,
            clip_count: variables.clipIds.length,
          },
        })
      }
    },
  })
}
